import React from 'react';
import { Box, Grid, Icon, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { ReactComponent as VisaIcon } from 'common/assets/icons/visa_official.svg';
import { ReactComponent as McIcon } from 'common/assets/icons/mc_official.svg';
import { ReactComponent as GooglePayIcon } from 'common/assets/icons/icon-googlepay.svg';
import { ReactComponent as ApplePayIcon } from 'common/assets/icons/icon-applepay.svg';
import { ReactComponent as TrustlyIcon } from 'common/assets/icons/icon-trustly-white.svg';
import LockIllustration from 'common/assets/illu-payments-lock.png';
import CardIllustration from 'common/assets/illu-payments-cards.png';
import {
	getDefaultCommission,
	getShopCustomPaymentCommission,
	planToPaymentPlan,
} from 'common/constants/paymentCommissions';
import { useSelector } from 'react-redux';

import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization';
import { getPaymentCommissionString } from 'common/modules/payments/utils';
import classNames from 'classnames';
import { PaymentProvider, PaymentProviders } from 'common/modules/payments/constants';

type PaymentMethodIntroProps = {
	paymentProvider: PaymentProvider;
	paymentMethods: string;
	description?: string;
	deposits?: boolean;
};

export const PaymentMethodIntro = (props: PaymentMethodIntroProps) => {
	const { paymentProvider, paymentMethods, description, deposits } = props;
	const classes = useStyles();
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down('sm'));
	const shop = useSelector(ShopSelectors.activeShopData)!;
	const plan = useSelector(ShopSelectors.shopPlan);
	const shopPaymentCommission = useSelector(ShopSelectors.activeShopPaymentMethodCommission);
	const paymentPlan = planToPaymentPlan(plan);
	const shopCurrency = shop.publicInfo.currency;
	const { t } = useTranslation();
	const paymentType =
		paymentProvider === PaymentProviders.adyen ? 'CARD_ONLINE' : 'CARD_ONLINE_STRIPE';
	const cardCommission =
		getShopCustomPaymentCommission(paymentType, shopPaymentCommission) ??
		getDefaultCommission(paymentPlan, shopCurrency.code, paymentType);

	const commissionTexts = {
		[PaymentProviders.adyen]: getPaymentCommissionString(cardCommission),
		[PaymentProviders.stripe]: `${getPaymentCommissionString(cardCommission)} (${t(
			'account.payments.exclStripeFees',
			'Excl. Stripe fees',
		)})`,
	};

	return (
		<Box mt={2}>
			<Grid container justifyContent="space-between" spacing={4}>
				<Grid item xs={12} sm={6}>
					{description && (
						<Typography variant="body2" color="textSecondary">
							{description}
						</Typography>
					)}
					<Box mt={description ? 3 : 0}>
						<PaymentMethodIcons paymentProvider={paymentProvider} />
					</Box>
					<Box mt={3}>
						<Typography variant="body1">
							<Box component="span" fontWeight={500}>
								{t('shop.paymentMethods', 'Payment methods')}
							</Box>
						</Typography>
						<Typography variant="body2" color="textSecondary">
							{paymentMethods}
						</Typography>
					</Box>
					<Box mt={3}>
						<Typography variant="body1">
							<Box component="span" fontWeight={500}>
								{t('account.payments.cardTransactionFee', 'Card transaction fee')}
							</Box>
						</Typography>
						<Typography variant="body2" color="textSecondary">
							{commissionTexts[paymentProvider]}
						</Typography>
					</Box>
				</Grid>
				<Grid
					container
					item
					xs={12}
					sm={6}
					justifyContent={isXs ? 'flex-start' : 'flex-end'}
					spacing={3}
				>
					{deposits && (
						<Grid item>
							<Box className={classNames(classes.illustrationWrapper, classes.lockIllustration)}>
								<Typography className={classes.illustrationLabel} variant="body1">
									{t('account.payments.deposits', 'Deposits')}
								</Typography>
							</Box>
						</Grid>
					)}
					<Grid item>
						<Box className={classNames(classes.illustrationWrapper, classes.cardIllustration)}>
							<Typography className={classes.illustrationLabel} variant="body1">
								{t('account.payments.cardPayments', 'Card payments')}
							</Typography>
						</Box>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
};

type PaymentMethodIconProps = {
	paymentProvider: PaymentProvider;
};

const PaymentMethodIcons = (props: PaymentMethodIconProps) => {
	const { paymentProvider } = props;
	const classes = useStyles();
	const icons =
		paymentProvider === PaymentProviders.adyen
			? [<VisaIcon />, <McIcon />, <ApplePayIcon />, <GooglePayIcon />, <TrustlyIcon />]
			: [<VisaIcon />, <McIcon />];
	return (
		<Box ml={-1} mr={-1} display="flex">
			{icons.map((icon, index) => (
				<Box key={index} ml={1} mr={1} display="flex">
					<Icon fontSize="medium" className={classes.svgIconParent}>
						{icon}
					</Icon>
				</Box>
			))}
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		svgIconParent: {
			'& svg': {
				maxWidth: '100%',
				height: '100%',
			},
			justifyContent: 'flex-end',
			fontSize: '4.5rem',
		},
		illustrationWrapper: {
			backgroundColor: theme.palette.background.secondary,
			borderRadius: 8,
			width: 216,
			height: 108,
			background: `${theme.palette.background.secondary} 100% 100%/contain no-repeat`,
			padding: 22,
		},
		lockIllustration: {
			backgroundImage: `url(${LockIllustration})`,
		},
		cardIllustration: {
			backgroundImage: `url(${CardIllustration})`,
		},
		illustrationLabel: {
			fontWeight: 500,
			fontSize: '1.6rem',
			lineHeight: 'normal',
			maxWidth: 100,
			wordBreak: 'break-word',
			hyphens: 'auto',
		},
	}),
);
